if (!customElements.get('panacea-sticky-atc')) {
  customElements.define(
    'panacea-sticky-atc',
    class PanaceaStickyAtc extends HTMLElement {
      constructor() {
        super();

        this.variants = [];
        this.unsubscribeVariantChange = null;
        this.mainFormObserver = null;
        this.mainFormVisible = false;
        this.scrollThreshold = Number(this.dataset.scrollThreshold || 480);
      }

      connectedCallback() {
        this.productForm = this.querySelector('product-form');
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.variantInput = this.querySelector('.product-variant-id');
        this.quantityInput = this.querySelector('[name="quantity"]');
        this.titleNode = this.querySelector('[data-panacea-title]');
        this.variantNode = this.querySelector('[data-panacea-variant]');
        this.priceNode = this.querySelector('[data-panacea-price]');
        this.comparePriceNode = this.querySelector('[data-panacea-compare-price]');
        this.imageNode = this.querySelector('[data-panacea-image]');
        this.submitButton = this.querySelector('[data-panacea-submit]');
        this.submitButtonLabel = this.querySelector('[data-panacea-submit-label]');
        this.statusNode = this.querySelector('[data-panacea-status]');

        this.readVariants();
        this.findMainProductForm();
        this.bindQuantitySync();
        this.bindVariantSync();
        this.bindVisibility();

        this.updateFromCurrentContext();
      }

      disconnectedCallback() {
        if (typeof this.unsubscribeVariantChange === 'function') {
          this.unsubscribeVariantChange();
        }

        if (this.mainFormObserver) {
          this.mainFormObserver.disconnect();
        }

        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
      }

      readVariants() {
        const dataNode = this.querySelector('[data-panacea-variants]');

        if (!dataNode) return;

        try {
          this.variants = JSON.parse(dataNode.textContent);
        } catch (error) {
          this.variants = [];
          console.warn('Panacea sticky ATC could not parse variant data.', error);
        }
      }

      findMainProductForm() {
        const forms = Array.from(document.querySelectorAll('form[data-type="add-to-cart-form"]'));

        this.mainForm = forms.find((form) => !this.contains(form)) || null;
        this.mainQuantityInput = document.querySelector('.product__info-container .quantity__input');
      }

      bindQuantitySync() {
        if (!this.quantityInput || !this.mainQuantityInput) return;

        const syncQuantity = () => {
          const nextValue = this.mainQuantityInput.value || this.quantityInput.min || '1';
          this.quantityInput.value = nextValue;
        };

        syncQuantity();
        this.mainQuantityInput.addEventListener('input', syncQuantity);
        this.mainQuantityInput.addEventListener('change', syncQuantity);
      }

      bindVariantSync() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (variant) this.updateForVariant(variant);
          });
        }

        window.addEventListener('popstate', () => this.updateFromCurrentContext());
      }

      bindVisibility() {
        this.onViewportChange = this.updateVisibility.bind(this);

        if (this.mainForm && 'IntersectionObserver' in window) {
          this.mainFormObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              this.mainFormVisible = Boolean(entry && entry.isIntersecting);
              this.updateVisibility();
            },
            {
              threshold: 0.2,
            }
          );

          this.mainFormObserver.observe(this.mainForm);
        }

        window.addEventListener('scroll', this.onViewportChange, { passive: true });
        window.addEventListener('resize', this.onViewportChange);
        this.updateVisibility();
      }

      updateVisibility() {
        const beyondThreshold = window.scrollY > this.scrollThreshold;
        const shouldShow = beyondThreshold && !this.mainFormVisible;
        this.classList.toggle('is-visible', shouldShow);
      }

      updateFromCurrentContext() {
        const variantFromUrl = this.getVariantFromUrl();
        const mainVariantId = this.mainForm?.querySelector('[name="id"]')?.value;
        const stickyVariantId = this.variantInput?.value;
        const fallbackVariantId =
          variantFromUrl || mainVariantId || stickyVariantId || this.dataset.defaultVariantId;

        const variant = this.getVariantById(fallbackVariantId);

        if (variant) {
          this.updateForVariant(variant);
        }
      }

      getVariantFromUrl() {
        const params = new URLSearchParams(window.location.search);
        return params.get('variant');
      }

      getVariantById(id) {
        if (!id) return null;

        const normalizedId = String(id);
        return this.variants.find((variant) => String(variant.id) === normalizedId) || null;
      }

      updateForVariant(variant) {
        if (this.variantInput) {
          this.variantInput.value = variant.id;
          this.variantInput.disabled = !variant.available;
        }

        const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        const money = this.formatMoney(variant.price);
        const compareMoney = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';

        if (this.variantNode) {
          this.variantNode.textContent = variantTitle;
          this.variantNode.hidden = !variantTitle;
        }

        if (this.priceNode) {
          this.priceNode.textContent = money;
        }

        if (this.comparePriceNode) {
          this.comparePriceNode.textContent = compareMoney;
          this.comparePriceNode.hidden = !compareMoney || variant.compare_at_price <= variant.price;
        }

        if (this.imageNode) {
          const imageSrc =
            variant.featured_image?.src ||
            variant.featured_media?.preview_image?.src ||
            this.imageNode.dataset.fallbackSrc;

          if (imageSrc) {
            this.imageNode.src = imageSrc;
          }
        }

        if (this.submitButton) {
          const isAvailable = Boolean(variant.available);
          this.submitButton.toggleAttribute('disabled', !isAvailable);
          this.submitButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        }

        if (this.submitButtonLabel) {
          this.submitButtonLabel.textContent = variant.available
            ? window.variantStrings?.addToCart || 'Agregar al carrito'
            : window.variantStrings?.soldOut || 'Agotado';
        }

        if (this.statusNode) {
          this.statusNode.textContent = variant.available
            ? this.dataset.availableLabel || ''
            : this.dataset.unavailableLabel || '';
        }
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || undefined);
        }

        const amount = Number(cents || 0) / 100;
        return new Intl.NumberFormat(document.documentElement.lang || 'es-MX', {
          style: 'currency',
          currency: this.dataset.currency || 'MXN',
        }).format(amount);
      }
    }
  );
}
